import { api } from "@signet/web-kit";
import type { Backfill, Kind } from "../shared/downloads.ts";
import type { Filters, RowRun, RunView } from "../shared/redownload.ts";
import type { PeriodId } from "../shared/period.ts";

/* What the page reads from /api/m/mainaccounts, typed once. Mirrors server/store.ts. */

export const BASE = "/api/m/mainaccounts";

export type AccountRow = {
  id: string;
  handle: string;
  status: "ok" | "private" | "missing";
  reading: boolean;
  posts: number;
  previous: number;
  views: number;
  spark: number[];
  followers: number | null;
  main: boolean;
};

export type CreatorRow = { creator: string; posts: number; previous: number; spark: number[]; accounts: AccountRow[]; mainPicked: boolean };

export type Overview = { days: string[]; creators: CreatorRow[] };

/** The browser's own time zone, so the sparkline's days are the viewer's days. */
const zone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

export const keys = { overview: (period: PeriodId) => ["mainaccounts", "overview", period] as const, all: ["mainaccounts"] as const };

export const fetchOverview = (period: PeriodId) => api<Overview>(`${BASE}/overview?${new URLSearchParams({ period, tz: zone() })}`);

export const addAccount = (creator: string, handle: string) => api<{ id: string; handle: string; private: boolean; banned: boolean }>(`${BASE}/accounts`, { body: { creator, handle } });

export const setMain = (creator: string, accountId: string | null) => api<{ ok: true }>(`${BASE}/main/${encodeURIComponent(creator)}`, { method: "PUT", body: { accountId } });

export const removeAccount = (id: string) => api<{ ok: true }>(`${BASE}/accounts/${encodeURIComponent(id)}`, { method: "DELETE" });

/* Downloads. Mirrors server/downloads.ts. */

export type Folder = { id: string; folderId: string; folderName: string; kind: Kind; accountId: string | null; sent: number; queued: number; failed: number };

export type Downloads = { folders: Folder[]; accounts: { id: string; handle: string }[]; creatorUploads: boolean };

export const downloadKeys = {
  all: ["mainaccounts", "downloads"] as const,
  counts: (period: PeriodId) => ["mainaccounts", "downloads", { period }] as const,
  creator: (creator: string) => ["mainaccounts", "downloads", creator] as const,
};

export type DownloadCounts = { folders: Record<string, number>; creatorUploads: string[]; downloaded: Record<string, number> };

export const fetchDownloadCounts = (period: PeriodId) => api<DownloadCounts>(`${BASE}/downloads?${new URLSearchParams({ period })}`);

export const fetchDownloads = (creator: string) => api<Downloads>(`${BASE}/downloads/${encodeURIComponent(creator)}`);

export const checkFolder = (link: string) => api<{ id: string; name: string }>(`${BASE}/downloads/folder`, { body: { link } });

export const saveDownloads = (creator: string, folders: { id?: string; folderId: string; kind: Kind; accountId: string | null }[], backfill: Backfill | null, creatorUploads: boolean) =>
  api<{ added: number; changed: number; deleted: number; backfills: number }>(`${BASE}/downloads/${encodeURIComponent(creator)}`, { method: "PUT", body: { folders, backfill, creatorUploads } });

/* Re-Download. Mirrors server/redownload.ts. */

export type Destination = { folderId: string; name: string; duplicates: number };

export type Redownload = { accounts: { id: string; handle: string }[]; destinations: Destination[]; last: RunView | null };

export const redownloadKeys = {
  all: ["mainaccounts", "redownload"] as const,
  // Under all, so starting a run in the popup refreshes every row's button with it.
  rows: ["mainaccounts", "redownload", { rows: true }] as const,
  creator: (creator: string) => ["mainaccounts", "redownload", creator] as const,
};

export const fetchRedownloadRows = () => api<{ runs: Record<string, RowRun> }>(`${BASE}/redownload`);

export const confirmRedownload = (creator: string, id: string) => api<{ runs: Record<string, RowRun> }>(`${BASE}/redownload/${encodeURIComponent(creator)}/confirm`, { body: { id } });

export const fetchRedownload = (creator: string) => api<Redownload>(`${BASE}/redownload/${encodeURIComponent(creator)}`);

export const startRedownload = (creator: string, input: Filters & { destFolderId: string; duplicates: number; adjust: boolean }) =>
  api<{ id: string; last: RunView | null }>(`${BASE}/redownload/${encodeURIComponent(creator)}`, { body: input });

/* Download analytics. Mirrors server/analytics.ts. */

export type DayRow = { day: string; creator: string | null; photos: number; videos: number; total: number };
export type CreatorDayRow = { creator: string; photos: number; videos: number; total: number; uploaded: number };
export type FolderRow = { folderId: string; folderName: string; photos: number; videos: number; total: number };

export const analyticsKeys = {
  days: ["mainaccounts", "analytics"] as const,
  day: (day: string) => ["mainaccounts", "analytics", day] as const,
  creator: (day: string, creator: string) => ["mainaccounts", "analytics", day, creator] as const,
};

export const fetchDays = () => api<{ days: DayRow[] }>(`${BASE}/analytics`);

export const fetchDayCreators = (day: string) => api<{ creators: CreatorDayRow[] }>(`${BASE}/analytics/${encodeURIComponent(day)}`);

export const fetchDayFolders = (day: string, creator: string) => api<{ folders: FolderRow[] }>(`${BASE}/analytics/${encodeURIComponent(day)}/${encodeURIComponent(creator)}`);
